import jwtAxios, {sseRequest} from '@crema/services/auth/jwt-auth';
import UploadToClientUI from './UploadToClientUI';
import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import AppInfoView from '@crema/core/AppInfoView';
import {fetchError, showMessage} from 'redux/actions';
import {useDispatch} from 'react-redux';
import {Box, Modal, IconButton, Button, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  width: 400,
};

const UploadToClientLg = () => {
  console.log('🚀 UploadToClientLg component loaded - validation enabled');
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const {messages} = useIntl();
  const [domainSelected, setDomainSelected] = useState(null);
  const [domainBackendUrl, setDomainBackendUrl] = useState(null);

  // Stats for API and DB calling
  const [doneStatusCountAPI, setDoneStatusCountAPI] = useState(0);
  const [allStatusCountAPI, setAllStatusCountAPI] = useState(0);
  const [errorStatusCountAPI, setErrorStatusCountAPI] = useState(0);
  const [errorDataAPI, setErrorDataAPI] = useState([]);
  const [doneStatusCountDB, setDoneStatusCountDB] = useState(0);
  const [allStatusCountDB, setAllStatusCountDB] = useState(0);
  const [errorStatusCountDB, setErrorStatusCountDB] = useState(0);
  const [errorDataDB, setErrorDataDB] = useState([]);

  const [fileName, setFileName] = useState('');
  const [affiliateId, setAffiliateId] = useState([]);
  const [dominId, setDominId] = useState([]);
  const [customerId, setCustomerId] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [fileSelected, setFileSelected] = useState(false);
  const [archiveData, setArchiveData] = useState([]);
  const [archiveDatabaseData, setArchiveDatabaseData] = useState([]);

  const [customerName, setCustomerName] = useState(''); // For customer name
  const [openConfirmModal, setOpenConfirmModal] = useState(false); // Modal state

  const [uploadFinished, setUploadFinished] = useState(false);
  const [domainIdSelected, setDomainIdSelected] = useState(null);

  // File validation states
  const [validationError, setValidationError] = useState('');
  const [isFileValid, setIsFileValid] = useState(true);

  // Upload completion states
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);
  const [hasSuccessfulData, setHasSuccessfulData] = useState(false);

  console.log('📊 Current validation state:', {validationError, isFileValid});

  // Handle Dropdown Changes
  const onDropDownChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setCustomerId(valueSelected.key);
      setFileSelected(true);
      setCustomerName(valueSelected.label); // Set customer name
    } else {
      setCustomerId(null);
      setFileSelected(false);
      setCustomerName(''); // Reset customer name
    }
  };

  const onDropDownSearch = (searchField) => {
    setSearchTerm(searchField.target.value);
  };

  // Handle file selection with validation
  const handleFileSelection = async (files) => {
    console.log('🔍 handleFileSelection called with files:', files);
    setUploadedFiles(files);
    setValidationError('');
    setIsFileValid(true);
    setIsUploadCompleted(false);
    setHasSuccessfulData(false);

    if (files && files.length > 0) {
      const file = files[0];
      console.log('Processing file:', file.name);

      // Validate file extension first
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      console.log('File extension:', fileExtension);

      if (!['xlsx', 'xls'].includes(fileExtension)) {
        console.log('Invalid file extension');
        setValidationError(
          'Invalid file type. Only Excel files (.xlsx, .xls) are allowed.',
        );
        setIsFileValid(false);
        return;
      }

      // Validate file headers
      console.log('Starting header validation...');
      const validation = await validateFileHeaders(file);
      console.log('Validation result:', validation);

      if (!validation.valid) {
        setValidationError(validation.error);
        setIsFileValid(false);
      } else {
        setValidationError('');
        setIsFileValid(true);
      }
    }
  };

  // File validation function (same as main upload page)
  const validateFileHeaders = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, {type: 'array'});
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {header: 1});

          if (jsonData.length === 0) {
            resolve({valid: false, error: 'File is empty'});
            return;
          }

          const headers = jsonData[0];
          const requiredHeaders = [
            'Full Name',
            'Phone',
            'Age',
            'E-mail Address',
            'Country',
            'marks',
            'MPC_4',
            'data_source',
          ];

          // Normalize headers for comparison
          const normalizeHeader = (header) => {
            return header
              ?.toString()
              .toLowerCase()
              .trim()
              .replace(/[\s\-_]/g, '');
          };

          const headerMappings = {
            fullname: ['fullname', 'full_name', 'name'],
            phone: ['phone', 'phonenumber', 'phone_number'],
            age: ['age'],
            emailaddress: [
              'email',
              'emailaddress',
              'email_address',
              'e-mail',
              'e_mail',
            ],
            country: ['country'],
            marks: ['marks', 'mark'],
            mpc4: ['mpc_4', 'mpc4'],
            datasource: ['datasource', 'data_source', 'datasource'],
          };

          const missingHeaders = requiredHeaders.filter((requiredHeader) => {
            const normalizedRequired = normalizeHeader(requiredHeader);
            const possibleMatches = headerMappings[normalizedRequired] || [
              normalizedRequired,
            ];

            return !headers.some((header) => {
              const normalizedHeader = normalizeHeader(header);
              return possibleMatches.some(
                (match) => normalizedHeader === normalizeHeader(match),
              );
            });
          });

          if (missingHeaders.length > 0) {
            resolve({
              valid: false,
              error: `Missing: ${missingHeaders.join(', ')}`,
            });
          } else {
            resolve({valid: true});
          }
        } catch (error) {
          resolve({valid: false, error: 'Invalid Excel file'});
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Remanage Archive Data
  const remanageDataArchive = (input) => {
    try {
      console.log('Incoming input:', input);

      // Ensure input is parsed
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;

      // Extract and parse inner `data`
      const rawData = parsedInput.data;
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      console.log('data in remanageDataArchive', data);

      // Safely extract ID and determine status
      const id = data?.id || 'Unknown ID';
      const status =
        parsedInput.error || (id === 'Unknown ID' ? 'Error' : 'Success');

      console.log('Parsed Archive Data:', {id, status});
      return {id, status};
    } catch (error) {
      console.error('Error parsing archive data:', error);
      return {id: 'Unknown ID', status: 'Error'};
    }
  };

  const handleDomainChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setDomainSelected(valueSelected.key);
      const selectedDomain = dominId.find(
        (domain) => domain.key === valueSelected.key,
      );
      if (selectedDomain) {
        setDomainBackendUrl(selectedDomain.url);
        setDomainIdSelected(selectedDomain.key);
      }
      setCustomerId(valueSelected.key);
      getCustomers(1000, 0, searchTerm, valueSelected.key);
      onDropDownChange(_, valueSelected);
    } else {
      setDomainSelected(null);
      setDomainBackendUrl(null);
      setCustomerId(null);
      setAffiliateId([]);
      onDropDownChange(_, valueSelected);
    }
  };

  const sendFile = async () => {
    if (!uploadedFiles || uploadedFiles.length !== 1) {
      dispatch(fetchError('Please select only one file'));
      return;
    }

    // Check if file is valid before proceeding
    if (!isFileValid) {
      dispatch(
        fetchError('Please select a valid Excel file with correct headers'),
      );
      return;
    }

    // REMOVED: No longer check for hasSuccessfulData
    // The system will now always show the report, even if all records are duplicates/errors
    // This allows users to see what happened instead of getting blocked

    setFileName(uploadedFiles[0].name.split('.')[0]);

    const formData = new FormData();
    formData.append('myfile', uploadedFiles[0]);

    setIsLoading(true);
    setIsClicked(true);
    // Use the selected domain's backend URL or fallback to default
    console.log(domainBackendUrl);

    const baseUrl = domainBackendUrl || jwtAxios.defaults.baseURL;
    sseRequest(`data/upload-file/send-data/${customerId}`, {
      body: formData,
      baseUrl: baseUrl,
      onmessage: (message) => {
        if (message.event.includes('doneUploadData')) {
          const doneData = JSON.parse(message.data);

          if (doneData.status === 'API_Success') {
            // API success - show in API stats
            setDoneStatusCountAPI((prev) => prev + 1);
            setAllStatusCountAPI((prev) => prev + 1);
            setArchiveData((prev) => {
              const apiSuccessData = JSON.parse(doneData.data);
              const {id, status} = remanageDataArchive({
                data: apiSuccessData,
                status: 'API_Success',
              });
              return {...prev, [id]: {id, status}};
            });
          } else if (
            doneData.status === 'duplicate_sent' ||
            doneData.status === 'duplicate_free'
          ) {
            // Duplicate saved - show as error so user knows it was duplicated
            console.log(
              'Duplicate saved to duplicate_sent_data, showing as error',
            );
            setErrorStatusCountDB((prev) => prev + 1);
            setAllStatusCountDB((prev) => prev + 1);
            setErrorDataDB((prev) => [
              ...prev,
              {
                ...JSON.parse(doneData.data),
                error: 'Duplicate entry',
              },
            ]);
            setArchiveDatabaseData((prev) => {
              const {id, status} = remanageDataArchive({
                data: doneData.data,
                error: 'Duplicate entry',
              });
              return {...prev, [id]: {id, status}};
            });
          } else {
            // Database success - show in database stats
            setDoneStatusCountDB((prev) => prev + 1);
            setAllStatusCountDB((prev) => prev + 1);
            setArchiveDatabaseData((prev) => {
              const {id, status} = remanageDataArchive(message.data);
              return {...prev, [id]: {id, status}};
            });
          }
        } else if (message.event.includes('errorUploadData')) {
          // Check if this is an API error or database error
          const errorData = JSON.parse(message.data);

          if (errorData.apiError) {
            // API error - show in API stats
            setErrorStatusCountAPI((prev) => prev + 1);
            setAllStatusCountAPI((prev) => prev + 1);
            setErrorDataAPI((prev) => [
              ...prev,
              remanageDataError(message.data),
            ]);
            setArchiveData((prev) => {
              const apiErrorData = JSON.parse(errorData.data);
              const {id, status} = remanageDataArchive({
                data: apiErrorData,
                error: errorData.error,
              });
              return {...prev, [id]: {id, status}};
            });
          } else {
            // Database error (including duplicates) - handle normally
            setErrorStatusCountDB((prev) => prev + 1);
            setErrorDataDB((prev) => [
              ...prev,
              remanageDataError(message.data),
            ]);
            setAllStatusCountDB((prev) => prev + 1);
            setArchiveDatabaseData((prev) => {
              console.log(message.data);
              const {id, status} = remanageDataArchive(message.data);
              return {...prev, [id]: {id, status}};
            });
          }
        }

        if (message.event.includes('doneUploadAPI')) {
          setDoneStatusCountAPI((prev) => prev + 1);
          setAllStatusCountAPI((prev) => prev + 1);
          setArchiveData((prev) => {
            const {id, status} = remanageDataArchive(message.data);
            return {...prev, [id]: {id, status}};
          });
        } else if (message.event.includes('errorUploadAPI')) {
          setErrorStatusCountAPI((prev) => prev + 1);
          setErrorDataAPI((prev) => [...prev, remanageDataError(message.data)]);
          setAllStatusCountAPI((prev) => prev + 1);
          setArchiveData((prev) => {
            const {id, status} = remanageDataArchive(message.data);
            return {...prev, [id]: {id, status}};
          });
        } else if (message.event.includes('DataSent')) {
          // Handle API data sending (success or not)
          const data = JSON.parse(message.data);
          setDoneStatusCountAPI(data.success);
          setErrorStatusCountAPI(data.failed);
          setAllStatusCountAPI(data.success + data.failed);
          // Always mark as successful to allow archiving, even if 0 records sent
          // The report will show what happened (duplicates, errors, etc.)
          setHasSuccessfulData(true);
        }
        console.log('message.data');
        console.log(message.data);
      },
      onclose: () => {
        setIsLoading(false);
        setUploadFinished(true);
        setIsUploadCompleted(true);
        // hasSuccessfulData is already set by the event handlers above
        // No need to override it here
      },
      onerror: (error) => {
        dispatch(fetchError('Failed: ' + error?.message));
        setIsLoading(false);
      },
    });
  };

  const remanageDataError = (string) => {
    const jsonParsed = JSON.parse(string); // parses entire string to object

    let data;

    if (typeof jsonParsed.data === 'string') {
      try {
        data = JSON.parse(jsonParsed.data); // parses string if needed
      } catch {
        data = jsonParsed.data;
      }
    } else {
      data = jsonParsed.data; // already an object
    }

    // attach error info safely
    data.error = jsonParsed.error || jsonParsed.originalErrorData || null;

    return data;
  };

  const archiveDataCall = () => {
    const archiveEndpoint = '/archive/add-api';
    // Map archiveData to the required format with "id" and "status"
    const mappedArchiveData = Object.values(archiveData).map((entry) => ({
      id: entry.id,
      status: entry.status,
    }));

    const archivePayload = {
      archive: mappedArchiveData,
      customer_id: customerId,
      domain_id: domainIdSelected,
    };

    jwtAxios
      .post(archiveEndpoint, archivePayload)
      .then(() => {
        dispatch(showMessage('Data archived successfully'));
        setArchiveData([]);
      })
      .catch((error) => {
        dispatch(fetchError('Failed to archive data: ' + error.message));
      });
  };

  const archiveDataDatabaseCall = () => {
    const archiveEndpoint = '/archive/add-dataBase-Archive';
    // Map archiveData to the required format with "id" and "status"
    const mappedArchiveData = Object.values(archiveDatabaseData).map(
      (entry) => ({
        id: entry.id,
        status: entry.status,
      }),
    );

    const archivePayload = {
      archive: mappedArchiveData,
      name: fileName,
    };

    jwtAxios
      .post(archiveEndpoint, archivePayload)
      .then(() => {
        dispatch(showMessage('Database data archived successfully'));
        setArchiveDatabaseData([]);

        // After database archive, also call API archive if there's API data
        if (archiveData && Object.keys(archiveData).length > 0) {
          archiveDataApiCall();
        }
      })
      .catch((error) => {
        dispatch(
          fetchError('Failed to archive database data: ' + error.message),
        );
      });
  };

  const archiveDataApiCall = () => {
    const apiArchiveEndpoint = '/archive/add-api';
    // Map archiveData (API responses) to the required format with "id" and "status"
    const mappedArchiveData = Object.values(archiveData).map((entry) => ({
      id: entry.id,
      status: entry.status,
    }));

    const apiArchivePayload = {
      archive: mappedArchiveData,
      customer_id: customerId,
      domain_id: domainIdSelected,
    };

    console.log('API Archive Payload:', apiArchivePayload);

    jwtAxios
      .post(apiArchiveEndpoint, apiArchivePayload)
      .then(() => {
        dispatch(showMessage('API data archived successfully'));
        setArchiveData([]);
        console.log('API archive completed successfully');
      })
      .catch((error) => {
        dispatch(fetchError('Failed to archive API data: ' + error.message));
        console.error('Failed to archive API data:', error);
      });
  };

  useEffect(() => {
    if (uploadFinished) {
      // Always call both archive endpoints after upload completion
      // First archive database data, then API data
      if (archiveDatabaseData && Object.keys(archiveDatabaseData).length > 0) {
        archiveDataDatabaseCall();
      } else if (archiveData && Object.keys(archiveData).length > 0) {
        // If no database data to archive, just archive API data
        archiveDataCall();
      }
      setUploadFinished(false);
    }
  }, [uploadFinished]);

  const getDomins = (pageSize, pageNumber, search) => {
    const url = `domain/search?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${search}`;
    jwtAxios
      .get(url)
      .then((e) => {
        setDominId(
          e.data.data.map((item) => ({
            label: `${item.domain_name}`,
            key: item.id,
            url: item.domain_backend_url,
          })),
        );
      })
      .catch((e) => {
        dispatch(fetchError(e.message));
      });
  };

  const getCustomers = (pageSize, pageNumber, search, domainId) => {
    const url = `customer/search-with-ingration-only?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${search}&domainId=${domainId}`;
    jwtAxios
      .get(url)
      .then((e) => {
        console.log('🔍 Customer search response:', e.data);
        console.log('🔍 Response structure:', {
          data: e.data,
          dataData: e.data.data,
          dataDataType: typeof e.data.data,
          isArray: Array.isArray(e.data.data),
        });

        // Handle different response structures
        let customers = [];
        if (Array.isArray(e.data.data)) {
          customers = e.data.data;
        } else if (e.data.data && Array.isArray(e.data.data.data)) {
          customers = e.data.data.data;
        } else {
          console.error('❌ Unexpected response structure:', e.data);
          setAffiliateId([]);
          return;
        }

        setAffiliateId(
          customers.map((item) => ({
            label: `${item.user_name}${
              item.integration_type === 'trackbox'
                ? ' - ' + JSON.parse(item.additional_data_integration)?.ai
                : ''
            }`,
            key: item.id,
          })),
        );
      })
      .catch((e) => {
        console.error('❌ Error fetching customers:', e);
        dispatch(fetchError(e.message));
      });
  };

  useEffect(() => {
    const getData = setTimeout(() => {
      getDomins(10, 0, searchTerm);
    }, 1000);
    return () => clearTimeout(getData);
  }, []);

  // Modal Handlers
  const handleOpenModal = () => {
    if (!customerName) {
      dispatch(fetchError('Please select a customer before sending.'));
      return;
    }
    setOpenConfirmModal(true);
  };

  const handleCloseModal = () => {
    setOpenConfirmModal(false);
  };

  const confirmSendFile = () => {
    setOpenConfirmModal(false); // Close the modal
    sendFile(); // Call the existing sendFile function
  };

  return (
    <>
      <AppInfoView />
      <UploadToClientUI
        fileName={fileName}
        isClicked={isClicked}
        sendDataLoading={isLoading}
        setUploadedFiles={handleFileSelection} // Use validation handler
        handleDomainChange={handleDomainChange}
        uploadedFiles={uploadedFiles}
        functionToRun={handleOpenModal} // Open modal on button click
        domainSelected={domainSelected || null}
        messages={messages}
        allStatusCountDB={allStatusCountDB}
        doneStatusCountDB={doneStatusCountDB}
        errorStatusCountDB={errorStatusCountDB}
        errorDataDB={errorDataDB}
        allStatusCountAPI={allStatusCountAPI}
        doneStatusCountAPI={doneStatusCountAPI}
        errorStatusCountAPI={errorStatusCountAPI}
        errorDataAPI={errorDataAPI}
        affiliateId={affiliateId}
        dominId={dominId}
        onDropDownChange={onDropDownChange}
        fileSelected={fileSelected}
        onDropDownSearch={onDropDownSearch}
        // Add validation props
        validationError={validationError}
        isFileValid={isFileValid}
        // Add upload completion props
        isUploadCompleted={isUploadCompleted}
        hasSuccessfulData={hasSuccessfulData}
      />

      {/* Confirmation Modal */}
      <Modal
        open={openConfirmModal}
        aria-labelledby='confirm-dialog-title'
        aria-describedby='confirm-dialog-description'
        onClose={handleCloseModal}
      >
        <Box sx={modalStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Typography id='confirm-dialog-title' variant='h6'>
              Confirm Sending Data
            </Typography>
            <IconButton onClick={handleCloseModal} aria-label='close'>
              <CloseIcon />
            </IconButton>
          </div>
          <Typography id='confirm-dialog-description' sx={{mt: 2}}>
            Are you sure you want to send the data to{' '}
            <strong>{customerName}</strong>?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 4,
              gap: 2,
            }}
          >
            <Button
              onClick={handleCloseModal}
              color='secondary'
              variant='outlined'
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSendFile}
              color='primary'
              variant='contained'
            >
              Yes, Send
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UploadToClientLg;
